import styled from 'styled-components';

export const HostContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 48px 48px;
    width: 100%;
`;

/* ─── Top bar ─────────────────────────────────────────────────────────────── */

export const TopBar = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

export const LaunchBtn = styled.button`
    align-items: center;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 8px;
    color: #fff;
    cursor: pointer;
    display: flex;
    flex-shrink: 0;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    gap: 6px;
    padding: 8px 16px;
    text-transform: uppercase;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: rgba(255, 255, 255, 0.18);
        border-color: rgba(255, 255, 255, 0.4);
    }
`;

/* ─── Navigation strip ────────────────────────────────────────────────────── */

export const NavStrip = styled.div`
    align-items: center;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    display: flex;
    gap: 8px;
    justify-content: center;
    padding: 12px 12px;
    width: fit-content;
`;

export const NavBtn = styled.button`
    align-items: center;
    background: ${({ $primary }) => $primary ? '#fff' : 'rgba(255,255,255,0.08)'};
    border: 1px solid ${({ $primary }) => $primary ? 'transparent' : 'rgba(255,255,255,0.15)'};
    border-radius: 8px;
    color: ${({ $primary }) => $primary ? '#111' : 'rgba(255,255,255,0.8)'};
    cursor: pointer;
    display: flex;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    gap: 5px;
    padding: 8px 18px;
    transition: all 0.2s ease;

    &:hover:not(:disabled) { opacity: 0.8; }
    &:disabled { cursor: not-allowed; opacity: 0.3; }

    svg { height: 16px; width: 16px; }
`;

export const CardPosition = styled.span`
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 500;
    min-width: 60px;
    text-align: center;
`;

/* ─── Main layout: current card + controls ────────────────────────────────── */

export const MainLayout = styled.div`
    display: flex;
    gap: 20px;
    width: 100%;

    @media (max-width: 900px) {
        flex-direction: column;
    }
`;

/* ─── Active card panel ───────────────────────────────────────────────────── */

export const ActiveCardPanel = styled.div`
    align-items: center;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 0 0 260px;
    justify-content: center;
    padding: 20px;
`;

export const ActiveCardSwatch = styled.div`
    background: ${({ $color }) => $color ?? 'rgba(255,255,255,0.1)'};
    border-radius: 8px;
    height: 6px;
    opacity: 0.8;
    width: 100%;
`;

export const ActiveCardName = styled.h2`
    color: #fff;
    font-family: 'Roboto Condensed', sans-serif;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.1;
    text-align: center;
    text-transform: uppercase;
`;

export const ActiveCardMeta = styled.div`
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.05em;
    text-align: center;
    text-transform: uppercase;
`;

export const ActiveCardParallel = styled.div`
    align-items: center;
    background: ${({ $color }) => {
        if (!$color) return 'rgba(255,255,255,0.1)';
        const h = $color.replace('#', '');
        const n = parseInt(h, 16);
        return `rgba(${(n>>16)&255},${(n>>8)&255},${n&255},0.2)`;
    }};
    border: 1px solid ${({ $color }) => $color ?? 'rgba(255,255,255,0.3)'};
    border-radius: 99px;
    color: #fff;
    display: flex;
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    font-weight: 600;
    gap: 6px;
    letter-spacing: 0.04em;
    padding: 4px 12px;
    text-transform: uppercase;
`;

/* ─── Controls panel ──────────────────────────────────────────────────────── */

export const ControlsPanel = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 16px;
`;

export const ControlSection = styled.div`
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 20px;
`;

export const ControlLabel = styled.label`
    color: rgba(255, 255, 255, 0.45);
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

export const SoldToggle = styled.button`
    align-items: center;
    background: ${({ $sold }) => $sold ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.06)'};
    border: 1px solid ${({ $sold }) => $sold ? 'rgba(74,222,128,0.5)' : 'rgba(255,255,255,0.15)'};
    border-radius: 8px;
    color: ${({ $sold }) => $sold ? 'rgb(74,222,128)' : 'rgba(255,255,255,0.7)'};
    cursor: pointer;
    display: flex;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    gap: 6px;
    padding: 10px 16px;
    transition: all 0.2s ease;
    width: 100%;

    &:hover { opacity: 0.8; }
    svg { height: 16px; width: 16px; }
`;

export const PriceInput = styled.input`
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    box-sizing: border-box;
    color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 600;
    outline: none;
    padding: 10px 14px;
    transition: border-color 0.2s ease;
    width: 100%;

    &::placeholder { color: rgba(255,255,255,0.2); }
    &:focus { border-color: rgba(255,255,255,0.35); }

    /* Hide spin arrows */
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
`;

export const NotesInput = styled.textarea`
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    box-sizing: border-box;
    color: #fff;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    min-height: 80px;
    outline: none;
    padding: 10px 14px;
    resize: vertical;
    transition: border-color 0.2s ease;
    width: 100%;

    &::placeholder { color: rgba(255,255,255,0.2); }
    &:focus { border-color: rgba(255,255,255,0.35); }
`;

/* ─── Show config panel (CSV export params) ───────────────────────────────── */

export const ShowConfigPanel = styled.div`
    align-items: center;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    box-sizing: border-box;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 16px 20px;
    width: 100%;
`;

export const ShowConfigFields = styled.div`
    display: flex;
    flex: 1;
    flex-wrap: nowrap;
    gap: 12px;
    min-width: 0;
`;

export const ShowConfigActions = styled.div`
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: 10px;
`;

export const ExportBtn = styled.button`
    align-items: center;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 8px;
    color: #fff;
    cursor: pointer;
    display: flex;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    gap: 8px;
    height: 38px;
    padding: 0 16px;
    text-transform: uppercase;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.35;
    }

    &:not(:disabled):hover {
        background: rgba(255, 255, 255, 0.18);
        border-color: rgba(255, 255, 255, 0.4);
    }
`;

/* ─── Runlist ─────────────────────────────────────────────────────────────── */

export const RunlistSection = styled.div`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 320px;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
`;

export const RunlistItem = styled.div`
    align-items: center;
    background: ${({ $active }) => $active ? 'rgba(255,255,255,0.12)' : 'transparent'};
    border: 1px solid ${({ $active }) => $active ? 'rgba(255,255,255,0.25)' : 'transparent'};
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    gap: 10px;
    opacity: ${({ $dragging }) => $dragging ? 0.4 : 1};
    padding: 8px 12px;
    transition: all 0.15s ease;

    &:hover { background: rgba(255,255,255,0.08); }
`;

export const DragHandle = styled.div`
    align-items: center;
    color: rgba(255, 255, 255, 0.5);
    cursor: grab;
    display: flex;
    flex-shrink: 0;
    padding: 8px;
    touch-action: none;

    &:hover { color: rgba(255, 255, 255, 0.5); }
    &:active { cursor: grabbing; }
`;

export const RunlistSwatch = styled.div`
    background: ${({ $color }) => $color ?? '#fff'};
    border-radius: 100px;
    flex-shrink: 0;
    height: 32px;
    width: 8px;
`;

export const RunlistInfo = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    overflow: hidden;
`;

export const RunlistName = styled.span`
    color: rgba(255, 255, 255, 0.9);
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const RunlistParallel = styled.span`
    color: rgba(255, 255, 255, 0.4);
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const RunlistIndex = styled.span`
    color: rgba(255, 255, 255, 0.67);
    flex-shrink: 0;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    text-align: right;
    white-space: nowrap;
`;

export const RunlistSoldDot = styled.div`
    background: rgb(74, 222, 128);
    border-radius: 50%;
    flex-shrink: 0;
    height: 6px;
    width: 6px;
`;

export const EmptyState = styled.div`
    color: rgba(255, 255, 255, 0.3);
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    padding: 32px;
    text-align: center;

    p {
        color: rgba(255, 255, 255, 0.18);
        font-size: 12px;
        margin-top: 6px;
    }
`;
